/**
 * PATH: apps/qa/src/pages/area-execution/ItemCard.tsx
 * PURPOSE: Individual checklist item card with swipe, status buttons, notes, evidence
 */

import { useState, useMemo, memo } from 'react'
import {
  MinusCircle, AlertTriangle, Camera, MessageSquare,
  Lock, Shield, Image, CheckCircle2, XCircle, CircleDot,
  Loader2, MoreHorizontal, AlertCircle,
} from 'lucide-react'
import { QaItemStatusButtons, QaNoteThread, QaOwnerVerification, type QaNote } from '@quotech/ui'
import { cn } from '@/lib/utils'
import type { Evidence } from '@/types/qa'
import type { BackendItemStatus, ItemWithState, UIItemStatus } from './area-execution-types'
import { toUIStatus } from './area-execution-types'
import { useSwipeGestures } from './useSwipeGestures'
import { EvidenceGallery } from './EvidenceGallery'
import { ItemCardPhotoSources } from './ItemCardPhotoSources'
import { ItemChangeActions } from './ItemChangeActions'

interface ItemCardProps {
  item: ItemWithState; isActive: boolean; isLocked: boolean
  /** Accepts a backend status too, so the picker can send In Progress and F/A. */
  onStatusChange: (status: UIItemStatus | BackendItemStatus) => void
  onAddNote: (text: string) => void | Promise<void>
  onEditNote: (noteId: string, text: string) => void | Promise<void>
  onDeleteNote: (noteId: string) => void | Promise<void>
  onReadingChange: (value: string, unit: string) => void
  onTakePhoto: () => void; onCreateNCR: () => void; onRequestHoldPoint: () => void
  evidence: Evidence[]; onViewEvidence: (evidence: Evidence) => void
  onDeleteEvidence?: (evidenceId: string) => void | Promise<void>
  areaId?: string
  onItemMutated?: () => void
  onPickPhotoFile?: (file: File) => void | Promise<void>
  onPickPhotoAsset?: (assetId: string, filename?: string) => void | Promise<void>
  photoBusy?: boolean
  /**
   * Owner's confirmation of this check's completion claim. Optional so existing
   * callers keep compiling; without it the Yes/No still reads, minus the button.
   */
  onVerifyCompletion?: (verified: boolean) => void | Promise<void>
}

const noop = () => {}

const STATUS_CONFIG: Record<UIItemStatus, { icon: typeof CircleDot; color: string; bg: string }> = {
  pending: { icon: CircleDot, color: 'text-amber-500', bg: 'bg-amber-50 dark:bg-amber-900/20' },
  done: { icon: CheckCircle2, color: 'text-emerald-500', bg: 'bg-emerald-50 dark:bg-emerald-900/20' },
  failed: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50 dark:bg-red-900/20' },
  na: { icon: MinusCircle, color: 'text-slate-500', bg: 'bg-slate-50 dark:bg-slate-800/50' },
  hold: { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-900/20' },
  further_action: { icon: AlertTriangle, color: 'text-orange-500', bg: 'bg-orange-50 dark:bg-orange-900/20' },
}

export const ItemCard = memo(function ItemCard({
  item, isActive, isLocked, onStatusChange, onAddNote, onEditNote, onDeleteNote, onReadingChange, onTakePhoto, onCreateNCR, onRequestHoldPoint, evidence, onViewEvidence, onDeleteEvidence, onVerifyCompletion, areaId, onItemMutated, onPickPhotoFile, onPickPhotoAsset, photoBusy,
}: ItemCardProps) {
  const [showNotes, setShowNotes] = useState(false)
  const [showActions, setShowActions] = useState(false)
  const [readingValue, setReadingValue] = useState(item.captured_value || '')
  const [readingUnit, setReadingUnit] = useState(item.captured_unit || String(item.evidence_rules?.unit || ''))

  const { swipeOffset, handlers } = useSwipeGestures(
    () => !isLocked && onStatusChange('failed'),
    () => !isLocked && onStatusChange('done')
  )

  const status = toUIStatus(item.status)
  const StatusIcon = STATUS_CONFIG[status].icon
  const itemEvidence = evidence.filter(e => e.item_instance === item.id)
  // A note has no asset, so it lives beside the gallery rather than in it
  // (EvidenceGallery renders only type === 'photo').
  const itemNotes: QaNote[] = itemEvidence
    .filter(e => e.type === 'note')
    .map(e => ({ id: e.id, text: e.note_text || '', authorName: e.captured_by_name, at: e.captured_at }))

  const swipeStyle = useMemo(() => {
    if (!swipeOffset) return {}
    const absOffset = Math.abs(swipeOffset)
    const isLeft = swipeOffset < 0
    const progress = Math.min(absOffset / 100, 1)
    return {
      transform: `translateX(${swipeOffset}px)`,
      backgroundColor: isLeft ? `rgba(239, 68, 68, ${progress * 0.2})` : `rgba(34, 197, 94, ${progress * 0.2})`,
    }
  }, [swipeOffset])

  return (
    <div {...handlers} style={swipeStyle}
      className={cn('bg-card rounded-xl border shadow-sm transition-all duration-200 relative',
        isActive ? 'border-primary ring-2 ring-primary/20' : 'border-border',
        item.has_hold_point && 'ring-2 ring-amber-500/30')}>
      {/* Header */}
      <div className="px-4 py-3 flex items-start gap-3">
        <div className={cn('p-2 rounded-lg shrink-0', STATUS_CONFIG[status].bg)}>
          <StatusIcon className={cn('w-5 h-5', STATUS_CONFIG[status].color)} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-mono text-muted-foreground">#{item.item_number || '?'}</span>
            {item.has_hold_point && (
              <span className="px-1.5 py-0.5 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded font-medium flex items-center gap-1">
                <Shield className="w-3 h-3" />Hold Point
              </span>
            )}
            {Boolean(item.evidence_rules?.requires_photo || item.evidence_rules?.requires_reading) && <span className="text-xs text-red-500 font-medium">Evidence required</span>}
            {itemEvidence.length - itemNotes.length > 0 && (
              <span className="px-1.5 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded flex items-center gap-1">
                <Image className="w-3 h-3" />{itemEvidence.length - itemNotes.length}
              </span>
            )}
            {itemNotes.length > 0 && (
              <span className="px-1.5 py-0.5 text-xs bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded flex items-center gap-1">
                <MessageSquare className="w-3 h-3" />{itemNotes.length}
              </span>
            )}
          </div>
          <p className="text-sm font-semibold text-foreground mt-1">{item.title || 'Checklist Item'}</p>
          {item.product && <p className="mt-1 text-xs font-medium text-primary">{item.product}</p>}
          {item.requirement_text && <p className="mt-2 text-sm leading-relaxed text-foreground">{item.requirement_text}</p>}
          {item.acceptance_criteria && <div className="mt-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300"><strong>Accept when:</strong> {item.acceptance_criteria}</div>}
          {Boolean(item.evidence_rules?.citations) && <p className="mt-2 text-xs text-muted-foreground"><strong>Reference:</strong> {String(item.evidence_rules?.citations)}</p>}
          {/* In the header, not the active-only action block: the operator asked
              for a Yes/No that reads on every check, and a collapsed card is
              where a supervisor scans for what is still unsigned. The confirm
              button appears only for actors the endpoint will accept. */}
          <QaOwnerVerification
            className="mt-2"
            targetLabel={`${item.item_number || ''} ${item.title || 'item'}`.trim()}
            owner_verified={item.owner_verified}
            owner_verified_at={item.owner_verified_at}
            owner_verified_by_name={item.owner_verified_by_name}
            can_verify_completion={item.can_verify_completion}
            onVerify={onVerifyCompletion ?? noop}
            canVerify={onVerifyCompletion ? undefined : false}
          />
          {areaId && onItemMutated && (
            <ItemChangeActions
              item={item}
              areaId={areaId}
              disabled={isLocked}
              showEditActions={false}
              onMutated={onItemMutated}
            />
          )}
        </div>
        {item.isSyncing && <Loader2 className="w-4 h-4 animate-spin text-primary shrink-0" />}
      </div>

      {/* Actions (active + not locked) */}
      {isActive && !isLocked && (
        <div className="px-4 pb-3 space-y-3">
          <QaItemStatusButtons
            currentStatus={item.status}
            disabled={isLocked}
            onSelect={onStatusChange}
          />
          <div className="flex gap-2">
            <button onClick={() => setShowNotes(!showNotes)} className={cn('flex-1 py-3 rounded-lg transition-colors text-sm flex items-center justify-center gap-1.5 min-h-[44px]',
              showNotes ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground hover:bg-muted/80')}>
              <MessageSquare className="w-4 h-4" />Notes{itemNotes.length > 0 && ` (${itemNotes.length})`}
            </button>
            <button onClick={onTakePhoto} className="flex-1 py-3 rounded-lg bg-muted text-muted-foreground hover:bg-muted/80 transition-colors text-sm flex items-center justify-center gap-1.5 min-h-[44px]">
              <Camera className="w-4 h-4" />Photo
            </button>
            {onPickPhotoFile && onPickPhotoAsset && (
              <ItemCardPhotoSources
                busy={photoBusy}
                onPickFile={onPickPhotoFile}
                onPickAsset={onPickPhotoAsset}
              />
            )}
            <button onClick={() => setShowActions(!showActions)} className={cn('p-2.5 rounded-lg transition-colors', showActions ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground hover:bg-muted/80')}>
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </div>
          {Boolean(item.evidence_rules?.requires_reading) && (
            <div className="rounded-lg border border-border p-3">
              <label className="text-xs font-medium text-foreground" htmlFor={`reading-${item.id}`}>Measured reading</label>
              <div className="mt-1 flex gap-2">
                <input id={`reading-${item.id}`} value={readingValue} onChange={(event) => setReadingValue(event.target.value)}
                  inputMode="decimal" className="min-h-11 min-w-0 flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm" placeholder="Value" />
                <input value={readingUnit} onChange={(event) => setReadingUnit(event.target.value)}
                  className="min-h-11 w-24 rounded-lg border border-input bg-background px-3 py-2 text-sm" aria-label="Reading unit" placeholder="Unit" />
                <button type="button" onClick={() => onReadingChange(readingValue, readingUnit)}
                  className="min-h-11 rounded-lg bg-primary px-3 text-sm font-medium text-primary-foreground">Record</button>
              </div>
              {item.reading_recorded_at && <p className="mt-1 text-xs text-muted-foreground">Last recorded {new Date(item.reading_recorded_at).toLocaleString()}</p>}
            </div>
          )}
          {showActions && (
            <div className="flex gap-2 flex-wrap">
              {status === 'failed' && (
                <button onClick={onCreateNCR} className="flex-1 py-2 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors text-sm flex items-center justify-center gap-1.5">
                  <AlertCircle className="w-4 h-4" />Create NCR
                </button>
              )}
              {item.has_hold_point && (
                <button onClick={onRequestHoldPoint} className="flex-1 py-2 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors text-sm flex items-center justify-center gap-1.5">
                  <Shield className="w-4 h-4" />Request Sign-off
                </button>
              )}
            </div>
          )}
          {showNotes && (
            <QaNoteThread
              notes={itemNotes}
              onAdd={onAddNote}
              onEdit={onEditNote}
              onDelete={onDeleteNote}
              emptyLabel="No notes on this item yet."
            />
          )}
          <EvidenceGallery evidence={itemEvidence} onAddEvidence={onTakePhoto} onViewEvidence={onViewEvidence} onDelete={onDeleteEvidence} />
        </div>
      )}

      {isActive && isLocked && (
        <div className="px-4 pb-3">
          <div className="py-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 text-sm text-center flex items-center justify-center gap-2">
            <Lock className="w-4 h-4" />Area is locked - Read only
          </div>
        </div>
      )}

      {isActive && !isLocked && Math.abs(swipeOffset) > 30 && (
        <div className="absolute inset-y-0 flex items-center pointer-events-none">
          {swipeOffset > 30 && <div className="absolute left-4 text-emerald-500 font-bold text-lg animate-pulse">PASS →</div>}
          {swipeOffset < -30 && <div className="absolute right-4 text-red-500 font-bold text-lg animate-pulse">← FAIL</div>}
        </div>
      )}
    </div>
  )
})
